import { useState, useRef, useCallback, useEffect } from "react";
import { useAppStore } from "./useAppStore"; // To potentially show errors

// Optional: Define a type for the avatar integration if you plan to use it
interface AvatarSpeaker {
  speak: (audioBlob: Blob, lipSync?: boolean) => Promise<void>;
}

// Check if a global avatar object exists (like in the original code)
const globalAvatar = (window as any).avatar as AvatarSpeaker | undefined;

export function useAudioPlayer() {
  const [isPlaying, setIsPlaying] = useState(false);
  const playbackContextRef = useRef<AudioContext | null>(null);
  const currentAudioSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const audioQueueRef = useRef<Blob[]>([]);
  const showQosMessage = useAppStore((state) => state.showQosMessage); // Get error reporting function

  // --- Core Playback Logic ---

  const playNextAudio = useCallback(async () => {
    if (audioQueueRef.current.length === 0 || isPlaying) {
      // console.log("Player: Queue empty or already playing.");
      return;
    }

    // Check for browser AudioContext support
    if (!window.AudioContext && !window.webkitAudioContext) {
      showQosMessage("Browser does not support AudioContext", -1);
      console.error("Browser does not support AudioContext.");
      audioQueueRef.current = []; // Clear queue as we can't play
      return;
    }

    setIsPlaying(true);
    const rawBlob = audioQueueRef.current.shift();

    if (!rawBlob) {
      setIsPlaying(false);
      return; // Should not happen due to initial check, but safety first
    }

    // Using optional avatar integration if available
    if (globalAvatar && globalAvatar.speak) {
      console.log("Player: Using avatar.speak()");
      try {
        await globalAvatar.speak(rawBlob, true); // Assuming true enables lip-sync
      } catch (error) {
        console.error("Player: Avatar speak error:", error);
        showQosMessage("Avatar playback error", 3000);
      } finally {
        setIsPlaying(false);
        // Use requestAnimationFrame to avoid potential call stack overflow if errors happen rapidly
        requestAnimationFrame(playNextAudio);
      }
      return; // Avatar handled playback
    }

    // --- Standard AudioContext Playback ---
    console.log("Player: Using standard AudioContext playback");
    let arrayBuffer: ArrayBuffer | null = null;
    let wavSampleRate: number | null = null;

    try {
      // 1. Get ArrayBuffer and Sample Rate
      arrayBuffer = await rawBlob.arrayBuffer();
      const dataView = new DataView(arrayBuffer);

      // Check for RIFF and WAVE headers
      if (
        dataView.getUint32(0, false) !== 0x52494646 || // "RIFF"
        dataView.getUint32(8, false) !== 0x57415645
      ) {
        // "WAVE"
        throw new Error("Invalid WAV file header.");
      }
      // Read sample rate (assuming standard WAV format)
      wavSampleRate = dataView.getUint32(24, true); // 24 is offset for Sample Rate, true for little-endian
      console.log(`Player: WAV Sample Rate: ${wavSampleRate}`);

      if (!wavSampleRate || wavSampleRate <= 0) {
        throw new Error(`Invalid sample rate read from WAV: ${wavSampleRate}`);
      }

      // 2. Ensure AudioContext matches sample rate
      if (
        !playbackContextRef.current ||
        playbackContextRef.current.sampleRate !== wavSampleRate
      ) {
        console.log(
          `Player: Creating/Recreating AudioContext for sample rate ${wavSampleRate}`
        );
        if (playbackContextRef.current) {
          await playbackContextRef.current.close(); // Close previous context if exists
        }
        // Use specific sample rate constructor
        playbackContextRef.current = new (window.AudioContext ||
          window.webkitAudioContext)({
          sampleRate: wavSampleRate,
        });
      }
      const context = playbackContextRef.current;

      // 3. Decode Audio Data
      const audioBuffer = await context.decodeAudioData(arrayBuffer.slice(0)); // Pass a copy

      // 4. Create Buffer Source and Play
      const source = context.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(context.destination);

      // Store the source node to allow stopping it
      currentAudioSourceRef.current = source;

      source.onended = () => {
        // console.log("Player: Playback finished.");
        currentAudioSourceRef.current = null; // Clear reference
        setIsPlaying(false);
        // Use requestAnimationFrame to avoid potential call stack overflow
        requestAnimationFrame(playNextAudio);
      };

      source.start(0); // Start playback immediately
      // console.log("Player: Playback started.");
    } catch (error: any) {
      console.error("Player: Error during playback:", error);
      showQosMessage(`Audio Playback Error: ${error.message}`, 5000);
      // Clean up and try next
      currentAudioSourceRef.current = null;
      setIsPlaying(false);
      // Use requestAnimationFrame to avoid potential call stack overflow
      requestAnimationFrame(playNextAudio);
    }
  }, [isPlaying, showQosMessage]); // Dependencies

  // --- Public API ---

  const queueAudio = useCallback(
    (audioBlob: Blob) => {
      if (!(audioBlob instanceof Blob)) {
        console.error(
          "Player: Invalid audio data passed to queueAudio. Expected Blob."
        );
        return;
      }
      // console.log(`Player: Queuing audio blob, size: ${audioBlob.size}`);
      audioQueueRef.current.push(audioBlob);
      playNextAudio(); // Attempt to start playback if not already playing
    },
    [playNextAudio]
  ); // Dependency

  const stopAllAudio = useCallback(() => {
    console.log("Player: Stopping all audio.");
    // Clear the queue
    audioQueueRef.current = [];

    // Stop the currently playing source node, if any
    if (currentAudioSourceRef.current) {
      try {
        currentAudioSourceRef.current.onended = null; // Remove listener to prevent triggering playNextAudio
        currentAudioSourceRef.current.stop();
        currentAudioSourceRef.current.disconnect();
      } catch (e) {
        console.error("Player: Error stopping audio source:", e);
      } finally {
        currentAudioSourceRef.current = null;
      }
    }

    // Reset playing state immediately
    setIsPlaying(false);

    // Note: We don't close the AudioContext here, as it might be needed for subsequent plays.
    // It will be recreated if the next audio has a different sample rate.
  }, []); // No dependencies needed

  // --- Cleanup ---
  useEffect(() => {
    // Return a cleanup function to be called when the component unmounts
    return () => {
      console.log("Player: Cleaning up audio player hook.");
      stopAllAudio(); // Stop any playback and clear queue
      if (playbackContextRef.current) {
        playbackContextRef.current
          .close()
          .catch((e) => console.error("Error closing AudioContext:", e));
        playbackContextRef.current = null;
      }
    };
  }, [stopAllAudio]); // Ensure stopAllAudio is stable

  return {
    isPlaying,
    queueAudio,
    stopAllAudio,
  };
}
